/**
 * Centralized React Query invalidation for anything that touches the vehicle fleet.
 *
 * Every time a vehicle is created, updated, deleted, or has one of its satellite
 * records changed (assignment, insurance, resource reading, …) we need the fleet
 * screens to show the new state *everywhere at once*: the main list, the
 * "Assured/Not assured" badge, issue counters, locations, related claims and
 * maintenance. Copy-pasting a partial invalidation list in each handler led to
 * screens showing stale data depending on which handler fired.
 *
 * Use this helper in any handler that modifies fleet state:
 *
 *   import { invalidateFleetData } from '../../lib/fleetInvalidation.js';
 *   const onSuccess = () => invalidateFleetData(queryClient);
 *
 * When refetchActive is true (default), active observers refetch immediately
 * so the UI updates without waiting for a focus/visibility event.
 */

// Every top-level query key that derives from vehicle state. Keep this list
// the single source of truth — if a new derived query is added (dashboard
// widget, KPI, etc.), register its root key here.
export const FLEET_QUERY_ROOTS = [
  'vehicles',
  'vehicleLocations',
  'vehicleInsurance',
  'vehicleExits',
  'trulyInsuredVehicleIdsSet',
  'insuranceVerificationBulk',
  'resourceAlertsSummary',
  'trips',
  'claims',
  'maintenance',
  'documents',
  'fines',
  'drivers',
  'organizations',
  'workingSites',
];

export function invalidateFleetData(queryClient, { refetchActive = true } = {}) {
  if (!queryClient) return;
  for (const root of FLEET_QUERY_ROOTS) {
    queryClient.invalidateQueries({
      queryKey: [root],
      refetchType: refetchActive ? 'active' : 'none',
    });
  }
}
